import React from "react";
import { MMB, PAGE, QUERY_PARAMS } from "./constants.js";
import "./mmb.css";
import isDuringMarketTime from "./utils.js";
import { sendProxyDiscordRequest, bufferToBase64Image } from "./utils.js";
import EnlargeableImage from "./enlargeable-image.js";
import FitController, {
  measureHeight,
  measureTop,
  MIN_IMAGE_HEIGHT,
  FALLBACK_HEADER_HEIGHT,
} from "./fit-controller.js";

// Both layouts stack two charts in a column, so each one gets half of the space
// left below the navbar, minus its own header bar.
const CHARTS_PER_COLUMN = 2;

export default class MMBViewer extends React.Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(window.location.search);
    this.state = {
      proxyDiscordImage: query.get(QUERY_PARAMS.PROXY_DISCORD_IMAGE) ? true : false,
      currentMMB: props.mmb ? props.mmb : MMB.SPY,
      currentIndex: 0,
      inMemoryMMB: [],
      mmbIndex: props.mmbIndex,
      marketCalendar: props.marketCalendar,
    };

    this.cache = {};

    this.containerRef = React.createRef();
    this.headerRef = React.createRef();
    this.fit = new FitController({
      getSignature: () => this.getFitSignature(),
      applyFit: () => this.applyFit(),
    });

    this.leftKeyDownAPITriggered = false;
    this.rightKeyDownAPITriggered = false;

    this.fetchMMB().finally(() => this.eventInit());
  }

  eventInit() {
    if (this.isUnmounted) {
      return;
    }

    window.addEventListener("resize", this.handleResize);
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("touchstart", this.handleTouchStart);
    window.addEventListener("touchend", this.handleTouchEnd);

    this.refreshTimer = setInterval(() => {
      if (!isDuringMarketTime(this.state.marketCalendar)) {
        return;
      }

      var oldMMBSize = this.state.inMemoryMMB.length;
      var oldIndex = this.state.currentIndex;
      this.fetchMMB(PAGE.AFTER).then(() => {
        var newMMBSize = this.state.inMemoryMMB.length;
        if (newMMBSize > oldMMBSize && oldIndex == 0) {
          this.state.currentIndex = 0;
          this.setState(this.state);
        }
      });
    }, 60000);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.fit.cancel();
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("touchstart", this.handleTouchStart);
    window.removeEventListener("touchend", this.handleTouchEnd);
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.touchTimer) {
      clearInterval(this.touchTimer);
      this.touchTimer = null;
    }
  }

  handleResize = () => {
    this.resizeMMB();
  };

  handleKeyDown = (event) => {
    if (this.state.inMemoryMMB.length == 0) {
      return;
    }

    if (event.code == "ArrowRight") {
      this.moveRight();
    } else if (event.code == "ArrowLeft") {
      this.moveLeft();
    }
  };

  handleTouchStart = (event) => {
    if (this.state.inMemoryMMB.length == 0) {
      return;
    }

    const posX = event.touches[0].clientX;
    const posY = event.touches[0].clientY;

    if (posY <= window.innerHeight * 0.2) {
      return;
    }

    if (posX >= window.innerWidth * 0.9 && !this.touchTimer) {
      this.moveRight();
      this.touchTimer = setInterval(() => {
        this.moveRight();
      }, 100);
    } else if (posX <= window.innerWidth * 0.1 && !this.touchTimer) {
      this.moveLeft();
      this.touchTimer = setInterval(() => {
        this.moveLeft();
      }, 100);
    }
  };

  handleTouchEnd = () => {
    if (this.state.inMemoryMMB.length == 0) {
      return;
    }

    if (this.touchTimer) {
      clearInterval(this.touchTimer);
      this.touchTimer = null;
    }
  };

  moveRight() {
    if (this.state.currentIndex == 0) {
      if (!this.rightKeyDownAPITriggered) {
        this.rightKeyDownAPITriggered = true;
        this.fetchMMB(PAGE.AFTER)
          .then(() => {
            if (this.state.currentIndex > 0) {
              this.state.currentIndex--;
              this.setState(this.state);
            }
          })
          .finally(() => {
            this.rightKeyDownAPITriggered = false;
          });
      }
    } else {
      this.state.currentIndex--;
      this.setState(this.state);
    }
  }

  moveLeft() {
    if (this.state.currentIndex < this.state.inMemoryMMB.length - 1) {
      this.state.currentIndex++;
      this.setState(this.state);
    } else if (!this.leftKeyDownAPITriggered) {
      this.leftKeyDownAPITriggered = true;
      this.fetchMMB(PAGE.BEFORE)
        .then(() => {
          if (this.state.currentIndex < this.state.inMemoryMMB.length - 1) {
            this.state.currentIndex++;
            this.setState(this.state);
          }
        })
        .finally(() => {
          this.leftKeyDownAPITriggered = false;
        });
    }
  }

  fetchMMB(page) {
    return this.sendRequest(page)
      .then((res) => {
        const filtered = this.filterMMBs(res);
        if (page == PAGE.AFTER) {
          this.state.inMemoryMMB = filtered.concat(this.state.inMemoryMMB);
          this.state.currentIndex += filtered.length;
        } else {
          this.state.inMemoryMMB = this.state.inMemoryMMB.concat(filtered);
        }
        this.resizeMMB();
      })
      .catch((e) => {
        console.log(e);
        throw e;
      });
  }

  filterMMBs(mmbs) {
    const filtered = mmbs.filter((e) => e.attachments && e.attachments.length > 0);
    if (this.state.currentMMB.symbol === MMB.PI_SPY.symbol) {
      return filtered.filter(
        (e) => e.attachments[0].filename && e.attachments[0].filename.includes("SPY")
      );
    } else if (this.state.currentMMB.symbol === MMB.PI_QQQ.symbol) {
      return filtered.filter(
        (e) => e.attachments[0].filename && e.attachments[0].filename.includes("QQQ")
      );
    }
    return filtered;
  }

  sendRequest(page) {
    var url = this.state.currentMMB.url;
    var params = {};
    if (page == PAGE.BEFORE) {
      var lastMMB = this.state.inMemoryMMB[this.state.inMemoryMMB.length - 1];
      params = { before: lastMMB.id };
    } else if (page == PAGE.AFTER) {
      var firstMMB = this.state.inMemoryMMB[0];
      params = { after: firstMMB.id };
    }

    const key = url + JSON.stringify(params);
    if (key in this.cache) {
      const value = this.cache[key];
      if (Date.now() <= value.ttl) {
        return new Promise((resolve) => {
          resolve(value.data);
        });
      }
    }

    return sendProxyDiscordRequest(url, params).then((res) => {
      const filtered = this.filterMMBs(res.data);
      this.cache[key] = {
        data: filtered,
        ttl: Date.now() + 5000,
      };
      return filtered;
    });
  }

  resizeMMB() {
    if (this.state.inMemoryMMB.length == 0) {
      return;
    }

    // The displayed size is always derived from the intrinsic size, so repeated
    // resizes cannot drift away from the real aspect ratio.
    if (this.naturalWidth && this.naturalHeight) {
      this.fit.reset();
      this.applyFit();
      return;
    }

    var currentMMB = this.state.inMemoryMMB[this.state.currentIndex];
    const img = new Image();
    img.onload = () => {
      this.naturalWidth = img.naturalWidth;
      this.naturalHeight = img.naturalHeight;
      this.fit.reset();
      this.applyFit();
    };
    if (this.state.proxyDiscordImage) {
      sendProxyDiscordRequest(
        currentMMB.attachments[0].url,
        /* params= */ undefined,
        /* isImage= */ true
      ).then((res) => {
        img.src = bufferToBase64Image(res.data);
      });
    } else {
      img.src = currentMMB.attachments[0].url;
    }
  }

  applyFit() {
    if (this.isUnmounted || !this.naturalWidth || !this.naturalHeight) {
      return;
    }

    const ratio = this.calculateRatio(this.naturalWidth, this.naturalHeight);
    this.state.mmbWidth = ratio * this.naturalWidth;
    this.state.mmbHeight = ratio * this.naturalHeight;
    this.fit.markFitted();
    this.setState(this.state);
  }

  componentDidMount() {
    // The first fit can run before the refs exist, in which case it used
    // fallback measurements; this check redoes it with the real ones.
    this.fit.reset();
    this.fit.check();
  }

  componentDidUpdate() {
    this.fit.check();
  }

  getFitSignature() {
    return `${window.innerWidth}x${window.innerHeight}:${this.getHeaderHeight()}:${this.getColumnTop()}`;
  }

  getHeaderHeight() {
    return measureHeight(this.headerRef, FALLBACK_HEADER_HEIGHT);
  }

  // The charts of a column share the space below the navbar, so the budget is
  // measured from the column rather than from this chart alone.
  getColumnTop() {
    const container = this.containerRef.current;
    const column = container && container.parentElement;
    return measureTop(column ? { current: column } : this.containerRef);
  }

  getAvailableImageHeight() {
    const columnHeight = window.innerHeight - this.getColumnTop();
    const available = columnHeight / CHARTS_PER_COLUMN - this.getHeaderHeight();
    return Math.max(available, MIN_IMAGE_HEIGHT);
  }

  calculateRatio(imgWidth, imgHeight) {
    // maxWidthRatio caps how much of the window the mmb column may take, so a
    // layout sharing the row with tables keeps room for them.
    const maxWidthRatio = this.props.maxWidthRatio || 1;
    var widthRatio = (window.innerWidth * maxWidthRatio) / imgWidth;
    var heightRatio = this.getAvailableImageHeight() / imgHeight;
    return Math.min(widthRatio, heightRatio);
  }

  handleOptionChange(event) {
    if (event.target.value != this.state.currentMMB.symbol) {
      var mmb = MMB[event.target.value];
      localStorage.setItem(this.state.mmbIndex, mmb.symbol)
      // The new symbol may publish charts with different intrinsic dimensions.
      this.naturalWidth = null;
      this.naturalHeight = null;
      this.state.currentMMB = mmb;
      this.state.currentIndex = 0,
      this.state.inMemoryMMB = [],
      this.state.leftKeyDownAPITriggered = false,
      this.state.rightKeyDownAPITriggered = false,
      this.setState(this.state);
      this.fetchMMB();
    }
  }

  renderDropdown() {
    const options = [];
    for (var key in MMB) {
      var mmb = MMB[key];
      options.push(
        <option key={mmb.symbol} value={mmb.symbol}>
          {mmb.symbol}
        </option>
      );
    }
    return options;
  }

  render() {
    var timestamp = null;
    var mmbUrl = null;
    var isLatest = false;
    if (this.state.inMemoryMMB.length > 0) {
      var currentMMB = this.state.inMemoryMMB[this.state.currentIndex];
      var timeInUtc = currentMMB.timestamp;
      var options = {
        weekday: "long",
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: false,
        timeZone: "America/New_York",
      };
      timestamp = new Date(timeInUtc).toLocaleString("en-US", options);
      mmbUrl = currentMMB.attachments[0].url;
      if (this.state.currentIndex == 0) {
        isLatest = true;
      }
    }

    return (
      <div className="component-app" ref={this.containerRef}>
        <div className="header-bar" ref={this.headerRef}>
          <h2 className="inline">
            {" "}
            Symbol:
            <select
              className="symbol"
              value={this.state.currentMMB.symbol}
              onChange={(e) => this.handleOptionChange(e)}
            >
              {this.renderDropdown()}
            </select>
          </h2>
          <h2 className="inline">
            {timestamp} {isLatest && <b className="latest">(Latest)</b>}
          </h2>
        </div>
        <div>
          <EnlargeableImage
            width={this.state.mmbWidth}
            height={this.state.mmbHeight}
            src={this.state.proxyDiscordImage ? undefined : mmbUrl}
            proxyDiscordSrc={this.state.proxyDiscordImage ? mmbUrl : undefined}
          />
        </div>
      </div>
    );
  }
}
